import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { Users, CreditCard, Activity, User, Zap, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { cn } from '../../../lib/utils';
import { local } from '../../../utils/constants';

const serverBaseUrl = local ? "http://127.0.0.1:8000" : "https://backend.withsix.co";

const TeamOverview = ({ teamInfo, members, teamUsage, membersUsage, usageHistory, fetchUsageHistory, onTabChange }) => {
  const [timeFilter, setTimeFilter] = useState('month');
  const [chartData, setChartData] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    if (teamInfo?.id) {
      fetchUsageHistory(teamInfo.id, timeFilter);
    }
  }, [teamInfo, timeFilter, fetchUsageHistory]);

  useEffect(() => {
    if (usageHistory) {
      // Transform usage history for chart
      const data = usageHistory.map(item => ({
        key: new Date(item.timestamp * 1000).toLocaleDateString(),
        value: item.total_tokens
      }));
      setChartData(data);
    }
  }, [usageHistory]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'pending_payment': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'suspended': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'cancelled': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'pending_payment': return 'Payment Pending';
      case 'suspended': return 'Suspended';
      case 'cancelled': return 'Cancelled';
      default: return status || 'Unknown';
    }
  };

  const formatUsage = (usage) => {
    if (usage >= 1000000) return `${(usage / 1000000).toFixed(1)}M`;
    if (usage >= 1000) return `${(usage / 1000).toFixed(1)}k`;
    return usage;
  };

  const handlePayment = async () => {
    setPaymentError('');
    setPaymentLoading(true);

    // Open window immediately on user click to avoid popup blockers
    const checkoutWindow = window.open('about:blank', '_blank');

    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('web_access_token') || localStorage.getItem('vscode_jwt_token') || sessionStorage.getItem('jwt_token');

      if (!token) {
        checkoutWindow?.close();
        setPaymentError('Authentication required. Please log in again.');
        setPaymentLoading(false);
        return;
      }

      // Call /teams/create - for existing teams with pending_payment, this returns a new checkout URL
      const response = await fetch(`${serverBaseUrl}/teams/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          team_name: teamInfo.name || teamInfo.team_name,
          members: teamInfo.total_seats || members?.length || 2,
          billing_preference: teamInfo.billing_preference || 'yearly'
        })
      });

      if (response.ok) {
        const data = await response.json();

        if (data.checkout_url) {
          if (checkoutWindow) {
            checkoutWindow.location.href = data.checkout_url;
          } else {
            // Fallback if popup was blocked
            window.location.href = data.checkout_url;
          }
        } else {
          checkoutWindow?.close();
          setPaymentError('Unable to generate checkout URL. Please try again.');
        }
      } else {
        checkoutWindow?.close();
        const errorData = await response.json().catch(() => ({}));
        setPaymentError(errorData.message || errorData.detail || 'Failed to generate checkout. Please try again.');
      }
    } catch (err) {
      checkoutWindow?.close();
      console.error('Payment checkout error:', err);
      setPaymentError('Something went wrong. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div className="flex-1 bg-[#0a0a0f] p-8 overflow-y-auto">
      {/* Team Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-purple-600 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-primary/20">
            {teamInfo?.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">{teamInfo?.name}</h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" /> {members?.length || 0} Members
              </span>
              <span className="w-1 h-1 bg-white/20 rounded-full" />
              <Badge variant="outline" className={cn(getStatusColor(teamInfo?.status))}>
                {getStatusLabel(teamInfo?.status)}
              </Badge>
            </div>
          </div>
        </div>
        
      </div>

      {/* Payment Required Banner */}
      {teamInfo?.status === 'pending_payment' && (
        <Card className="mb-8 border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 to-orange-500/5">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                  Complete Your Team Setup
                  <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30 animate-pulse">
                    Payment Required
                  </Badge>
                </h3>
                <p className="text-muted-foreground">
                  Your team "{teamInfo.name || teamInfo.team_name}" is ready! Complete payment to activate {teamInfo.total_seats || members?.length || 2} seats.
                </p>
              </div>
            </div>
            <Button
              onClick={handlePayment}
              disabled={paymentLoading}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold shadow-lg shadow-yellow-500/20 ml-4 disabled:opacity-70"
            >
              {paymentLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Pay Now'
              )}
            </Button>
          </CardContent>
          {paymentError && (
            <div className="px-6 pb-4">
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {paymentError}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Usage</CardTitle>
            <Zap className="w-4 h-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{formatUsage(teamUsage?.total_tokens || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Tokens consumed</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Members</CardTitle>
            <User className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{members?.length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Team members</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Plan Status</CardTitle>
            <CreditCard className="w-4 h-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white uppercase">{teamInfo?.plan || 'Pro+'}</div>
            <p className="text-xs text-muted-foreground mt-1">{teamInfo?.billing_preference || teamInfo?.billing_cycle || 'monthly'} billing</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Top User</CardTitle>
            <Activity className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold text-white truncate">
              {membersUsage?.[0]?.fullname || 'No usage yet'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Most active member</p>
          </CardContent>
        </Card>
      </div>

      {/* Usage Chart */}
      <Card className="bg-[#121219]/80 border-white/10 mb-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Team Usage Trends</CardTitle>
            <CardDescription>Token consumption over time</CardDescription>
          </div>
          <div className="flex items-center gap-2 bg-white/5 p-1 rounded-lg border border-white/10">
            {['week', 'month', 'year'].map((filter) => (
              <button
                key={filter}
                onClick={() => setTimeFilter(filter)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                  timeFilter === filter 
                    ? "bg-primary text-white shadow-sm" 
                    : "text-muted-foreground hover:text-white hover:bg-white/5"
                )}
              >
                {filter === 'week' ? '7D' : filter === 'month' ? '30D' : '1Y'}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                <XAxis 
                  dataKey="key" 
                  stroke="#666" 
                  fontSize={12} 
                  tickLine={false}
                  axisLine={false}
                  dy={10}
                />
                <YAxis 
                  stroke="#666" 
                  fontSize={12} 
                  tickLine={false}
                  axisLine={false}
                  dx={-10}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1a1a24', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  itemStyle={{ color: '#fff' }}
                  labelStyle={{ color: '#a0a0a0' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#E005FF" 
                  strokeWidth={3}
                  dot={{ fill: '#E005FF', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#E005FF', strokeWidth: 2, fill: '#fff' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Members Usage Table */}
      <Card className="bg-[#121219]/80 border-white/10">
        <CardHeader>
          <CardTitle>Member Usage</CardTitle>
          <CardDescription>Individual contribution breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-muted-foreground uppercase bg-white/5">
                <tr>
                  <th className="px-4 py-3 rounded-l-lg">Member</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Total Usage</th>
                  <th className="px-4 py-3 rounded-r-lg text-right">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {membersUsage?.length > 0 ? (
                  membersUsage.map((member) => (
                    <tr key={member.user_id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white">
                            {member.fullname?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <div className="text-white font-medium">{member.fullname}</div>
                            <div className="text-xs text-muted-foreground">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="border-white/20 text-white/80 bg-white/5 capitalize">
                          {member.role || 'Member'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-white font-mono">
                        {formatUsage(member.total_tokens || 0)}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {member.last_active ? new Date(member.last_active * 1000).toLocaleDateString() : 'Never'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">
                      No usage data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamOverview;
